import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import settingsService from '../services/settingsService';

import { Alert, AlertDescription } from "../components/ui/alert"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog"
import { Button } from "../components/ui/button"

import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  Cloud,
  Loader2,
  Hourglass,
  Ban,
  MessageCircle,
  AlertCircle,
  CheckCircle2,
  HardDrive,
  Shield,
  Share2,
  ArrowRight,
} from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);
  const [statusDetails, setStatusDetails] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const [appSettings, setAppSettings] = useState(null);
  const [forgotPasswordDialogOpen, setForgotPasswordDialogOpen] = useState(false);

  const successMessage = location.state?.message;

  const saveCredentials = (email, password) => {
    try {
      localStorage.setItem('rememberedEmail', email);
      localStorage.setItem('rememberedPassword', btoa(password));
      localStorage.setItem('rememberMe', 'true');
    } catch (err) { console.error('Failed to save credentials:', err); }
  };

  const loadCredentials = () => {
    try {
      if (localStorage.getItem('rememberMe') === 'true') {
        const email = localStorage.getItem('rememberedEmail');
        const encodedPassword = localStorage.getItem('rememberedPassword');
        if (email && encodedPassword) {
          setFormData({ email, password: atob(encodedPassword) });
          setRememberMe(true);
        }
      }
    } catch (err) { console.error('Failed to load credentials:', err); }
  };

  const clearSavedCredentials = () => {
    try {
      localStorage.removeItem('rememberedEmail');
      localStorage.removeItem('rememberedPassword');
      localStorage.removeItem('rememberMe');
    } catch (err) { console.error('Failed to clear credentials:', err); }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setAccountStatus(null);
  };

  useEffect(() => { loadCredentials(); }, []);

  useEffect(() => {
    settingsService.getSettings().then(setAppSettings).catch(err => console.error('Failed to load settings:', err));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setAccountStatus(null);
    if (!formData.email || !formData.password) { setError('Email dan password harus diisi'); return; }
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      if (rememberMe) saveCredentials(formData.email, formData.password);
      else clearSavedCredentials();
      setLoading(false);
      const user = JSON.parse(localStorage.getItem('user'));
      navigate(user?.role === 'admin' ? '/admin' : '/dashboard');
    } catch (err) {
      setLoading(false);
      const response = err.response?.data;
      if (response?.status === 'pending') {
        setAccountStatus('pending');
        setStatusDetails({ registered_at: response.registered_at });
      } else if (response?.status === 'rejected') {
        setAccountStatus('rejected');
        setStatusDetails({ rejection_reason: response.rejection_reason, rejected_at: response.rejected_at });
      } else {
        setError(response?.message || 'Login gagal. Periksa email dan password Anda.');
      }
    }
  };

  // =========================================================
  // SHARED BACKGROUND STYLE
  // =========================================================
  const pageStyle = {
    display: 'flex',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  };

  const cardStyle = {
    background: '#fff',
    borderRadius: '20px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 32px rgba(0,0,0,0.07)',
    width: '100%',
    maxWidth: '440px',
  };

  const inputStyle = {
    width: '100%',
    height: '46px',
    borderRadius: '10px',
    border: '1.5px solid #e2e8f0',
    background: '#fff',
    padding: '0 14px 0 40px',
    fontSize: '14px',
    color: '#0f172a',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    boxSizing: 'border-box',
  };

  const primaryBtnStyle = {
    height: '44px',
    borderRadius: '10px',
    border: 'none',
    background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
    transition: 'all 0.2s ease',
  };

  const outlineBtnStyle = {
    height: '44px',
    width: '100%',
    borderRadius: '10px',
    border: '1.5px solid #e2e8f0',
    background: '#fff',
    color: '#374151',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  };

  // =========================================================
  // PENDING STATUS VIEW
  // =========================================================
  if (accountStatus === 'pending') {
    return (
      <div style={{ ...pageStyle, alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={cardStyle}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 32px', textAlign: 'center' }}>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, #fef3c7, #fde68a)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px', boxShadow: '0 4px 20px rgba(245,158,11,0.2)' }}>
              <Hourglass size={36} color="#d97706" />
            </div>
            <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}>Menunggu Persetujuan</h2>
            <p style={{ color: '#64748b', marginBottom: '16px', lineHeight: 1.6, fontSize: '14px' }}>
              Akun Anda sedang menunggu persetujuan dari administrator. Anda dapat login setelah akun disetujui.
            </p>
            {statusDetails?.registered_at && (
              <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '24px' }}>
                Terdaftar: {new Date(statusDetails.registered_at).toLocaleString('id-ID')}
              </p>
            )}
            <button style={outlineBtnStyle} onClick={() => { setAccountStatus(null); setFormData({ email: '', password: '' }); }}>
              Kembali ke Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // =========================================================
  // REJECTED STATUS VIEW
  // =========================================================
  if (accountStatus === 'rejected') {
    return (
      <div style={{ ...pageStyle, alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={cardStyle}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 32px', textAlign: 'center' }}>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, #fee2e2, #fca5a5)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px', boxShadow: '0 4px 20px rgba(239,68,68,0.2)' }}>
              <Ban size={36} color="#dc2626" />
            </div>
            <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#dc2626', marginBottom: '8px' }}>Pendaftaran Ditolak</h2>
            <p style={{ color: '#64748b', marginBottom: '16px', fontSize: '14px' }}>
              Maaf, pendaftaran akun Anda telah ditolak oleh administrator.
            </p>
            {statusDetails?.rejection_reason && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', textAlign: 'left', width: '100%' }}>
                <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Alasan:</p>
                <p style={{ fontSize: '14px', color: '#dc2626', fontWeight: 500 }}>{statusDetails.rejection_reason}</p>
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', width: '100%', marginTop: '8px' }}>
              <button style={{ ...outlineBtnStyle, flex: 1 }} onClick={() => { setAccountStatus(null); setFormData({ email: '', password: '' }); }}>Kembali</button>
              <button style={{ ...primaryBtnStyle, flex: 1 }} onClick={() => navigate('/register')}>Daftar Ulang</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // =========================================================
  // MAIN LOGIN VIEW — SPLIT SCREEN
  // =========================================================
  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>

      {/* ===== LEFT PANEL (dark branding) — hidden on mobile ===== */}
      {!isMobile && (
        <div style={{
          width: '42%',
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #0f172a 0%, #1e3a5f 60%, #0f2744 100%)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '48px 40px',
          position: 'relative',
          overflow: 'hidden',
          flexShrink: 0,
        }}>
          {/* Decorative circles */}
          <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(56,189,248,0.06)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: '-60px', left: '-60px', width: '220px', height: '220px', borderRadius: '50%', background: 'rgba(99,102,241,0.08)', pointerEvents: 'none' }} />

          {/* Logo / Brand */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '56px' }}>
              {!logoError && appSettings?.logo_url ? (
                <img src={appSettings.logo_url} alt="Logo" onError={() => setLogoError(true)}
                  style={{ height: '44px', width: 'auto', objectFit: 'contain' }} />
              ) : (
                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'linear-gradient(135deg, #38bdf8, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 1px rgba(255,255,255,0.1)' }}>
                  <Cloud size={22} color="#fff" />
                </div>
              )}
              <div>
                <div style={{ color: '#f1f5f9', fontWeight: 700, fontSize: '16px', lineHeight: 1.2 }}>
                  {appSettings?.app_name || 'Data Storage'}
                </div>
                <div style={{ color: '#38bdf8', fontWeight: 600, fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  {appSettings?.app_subtitle || 'PDTI-UMI'}
                </div>
              </div>
            </div>

            {/* Headline */}
            <h2 style={{ color: '#f1f5f9', fontSize: '28px', fontWeight: 800, lineHeight: 1.25, marginBottom: '16px' }}>
              Penyimpanan Data<br />Terpusat & Aman
            </h2>
            <p style={{ color: '#cbd5e1', fontSize: '14px', lineHeight: 1.7, marginBottom: '40px' }}>
              Kelola, bagikan, dan akses file Anda kapan saja dan di mana saja dengan keamanan tinggi.
            </p>

            {/* Feature highlights */}
            {[
              { icon: HardDrive, label: 'Penyimpanan terkelola', desc: 'Kuota storage personal terpisah per pengguna' },
              { icon: Share2, label: 'Berbagi mudah', desc: 'Bagikan file & folder dengan tautan aman' },
              { icon: Shield, label: 'Keamanan terjamin', desc: 'Akses dikelola & disetujui oleh administrator' },
            ].map(({ icon: Icon, label, desc }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '20px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={16} color="#38bdf8" />
                </div>
                <div>
                  <div style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: 600, marginBottom: '2px' }}>{label}</div>
                  <div style={{ color: '#94a3b8', fontSize: '12px', lineHeight: 1.5 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div style={{ color: '#64748b', fontSize: '11px' }}>
            © 2025 Dibuat oleh <span style={{ color: '#94a3b8', fontWeight: 600 }}>Muhammad Akbar Abdullah</span>
          </div>
        </div>

      )} {/* end !isMobile left panel */}

      {/* ===== RIGHT PANEL (login form) ===== */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isMobile
          ? 'linear-gradient(180deg, #0f172a 0%, #1e3a5f 36%, #f1f5f9 36%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        padding: isMobile ? '0 0 48px' : '40px 24px',
        alignItems: isMobile ? 'flex-start' : 'center',
        overflowY: 'auto',
        minHeight: '100vh',
      }}>
        <div style={{ width: '100%', maxWidth: '420px', padding: isMobile ? '0 20px' : '0' }}>

          {/* Mobile branding strip */}
          {isMobile && (
            <div style={{ padding: '28px 0 24px', textAlign: 'center' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg, #38bdf8, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Cloud size={18} color="#fff" />
                </div>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ color: '#f1f5f9', fontWeight: 700, fontSize: '14px', lineHeight: 1.2 }}>{appSettings?.app_name || 'Data Storage'}</div>
                  <div style={{ color: '#38bdf8', fontWeight: 600, fontSize: '10px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{appSettings?.app_subtitle || 'PDTI-UMI'}</div>
                </div>
              </div>
            </div>
          )}

          {/* Card wrapper on mobile */}
          <div style={isMobile ? {
            background: '#ffffff',
            borderRadius: '20px',
            padding: '28px 24px 32px',
            boxShadow: '0 -4px 40px rgba(0,0,0,0.15)',
          } : {}}>

            {/* Welcome header */}
            <div style={{ marginBottom: '28px' }}>
              <h1 style={{ fontSize: '24px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>
                Selamat datang
              </h1>
              <p style={{ color: '#64748b', fontSize: '14px' }}>
                Masuk untuk melanjutkan ke akun Anda
              </p>
            </div>

            {/* Success alert */}
            {successMessage && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderRadius: '10px', background: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: '20px' }}>
                <CheckCircle2 size={16} color="#16a34a" style={{ flexShrink: 0 }} />
                <span style={{ color: '#15803d', fontSize: '13px' }}>{successMessage}</span>
              </div>
            )}

            {/* Error alert */}
            {error && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderRadius: '10px', background: '#fef2f2', border: '1px solid #fca5a5', marginBottom: '20px' }}>
                <AlertCircle size={16} color="#dc2626" style={{ flexShrink: 0 }} />
                <span style={{ color: '#dc2626', fontSize: '13px' }}>{error}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>

              {/* Email field */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                  Alamat Email
                </label>
                <div style={{ position: 'relative' }}>
                  <Mail size={16} color="#94a3b8" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="email@contoh.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoFocus
                    style={inputStyle}
                    onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                    onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
              </div>

              {/* Password field */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                  Kata Sandi
                </label>
                <div style={{ position: 'relative' }}>
                  <Lock size={16} color="#94a3b8" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    style={{ ...inputStyle, paddingRight: '44px' }}
                    onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.1)'; }}
                    onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.boxShadow = 'none'; }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '4px', display: 'flex', alignItems: 'center' }}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Remember me + forgot password */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#64748b' }}>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={e => setRememberMe(e.target.checked)}
                    style={{ width: '15px', height: '15px', accentColor: '#3b82f6', cursor: 'pointer' }}
                  />
                  Ingat saya
                </label>
                <button
                  type="button"
                  onClick={() => setForgotPasswordDialogOpen(true)}
                  style={{ background: 'none', border: 'none', color: '#3b82f6', fontSize: '13px', fontWeight: 600, cursor: 'pointer', padding: 0 }}
                >
                  Lupa Kata Sandi?
                </button>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%', height: '48px', borderRadius: '12px', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                  background: loading ? '#94a3b8' : 'linear-gradient(135deg, #3b82f6, #6366f1)',
                  color: '#fff', fontSize: '15px', fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  boxShadow: loading ? 'none' : '0 4px 16px rgba(99,102,241,0.4)',
                  transition: 'all 0.2s ease',
                  marginTop: '4px',
                }}
                onMouseEnter={e => { if (!loading) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(99,102,241,0.5)'; } }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = loading ? 'none' : '0 4px 16px rgba(99,102,241,0.4)'; }}
              >
                {loading ? (
                  <><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Masuk...</>
                ) : (
                  <> Masuk <ArrowRight size={16} /></>
                )}
              </button>
            </form>

            {/* Divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '24px 0' }}>
              <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
              <span style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 500 }}>ATAU</span>
              <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
            </div>

            {/* Register link */}
            <div style={{ textAlign: 'center' }}>
              <span style={{ color: '#64748b', fontSize: '14px' }}>Belum punya akun? </span>
              <button
                onClick={() => navigate('/register')}
                style={{ background: 'none', border: 'none', color: '#3b82f6', fontSize: '14px', fontWeight: 700, cursor: 'pointer', padding: 0 }}
              >
                Daftar sekarang
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotPasswordDialogOpen} onOpenChange={setForgotPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Lupa Kata Sandi?</DialogTitle>
            <DialogDescription>
              Untuk mereset password, silakan hubungi administrator melalui kontak di bawah ini:
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', borderRadius: '12px', background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.15)' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(59,130,246,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Mail size={18} color="#3b82f6" />
              </div>
              <div>
                <p style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '2px' }}>Email Admin</p>
                <p style={{ fontWeight: 600, fontSize: '14px', color: '#0f172a' }}>admin@pdti.umi.ac.id</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', borderRadius: '12px', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <MessageCircle size={18} color="#16a34a" />
              </div>
              <div>
                <p style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '2px' }}>WhatsApp Admin</p>
                <p style={{ fontWeight: 600, fontSize: '14px', color: '#15803d' }}>085340852512</p>
              </div>
            </div>
            <div style={{ fontSize: '12px', color: '#94a3b8', paddingTop: '4px' }}>
              💡 <strong>Tip:</strong> Sertakan username atau email Anda saat menghubungi admin untuk mempercepat proses reset password.
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setForgotPasswordDialogOpen(false)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;